import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Calendar, User, Clock, ChevronRight, ChevronUp, ChevronDown,
  ArrowLeft, ArrowRight, Share2, MessageCircle, Mail, Link2, Check, List,
  Send,
} from 'lucide-react';

/* Brand marks — lucide-react in this project doesn't export social icons,
   so we inline minimal SVGs. All sized 15×15 to match the other lucide
   glyphs in the share bar. currentColor lets them inherit the button color. */
const FacebookMark = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.51 1.5-3.9 3.78-3.9 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12z"/>
  </svg>
);
const LinkedinMark = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.95v5.66H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.11 20.45H3.56V9h3.55v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"/>
  </svg>
);
const XMark = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import { MarkdownRenderer, extractHeadings } from '../components/MarkdownEditor';
import { CATEGORY_BY_ID } from '../config/blogCategories';

const API = process.env.REACT_APP_BACKEND_URL;
/* Public URL used for share links + canonical/JSON-LD. We use the production
   domain here intentionally so search bots index the canonical version even
   when somebody copy-shares a preview URL. */
const PUBLIC_BASE = 'https://casaothers.nl';

const BlogDetail = () => {
  const { slug } = useParams();
  const { language, t } = useLanguage();
  const isNL = language === 'nl';

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adjacent, setAdjacent] = useState({ previous: null, next: null });
  const [related, setRelated] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [tocOpen, setTocOpen] = useState(true);
  const [scrollPct, setScrollPct] = useState(0);
  const [copied, setCopied] = useState(false);
  const articleRef = useRef(null);

  /* Fetch + reset on slug change (covers in-page navigation between posts). */
  useEffect(() => {
    setLoading(true);
    setPost(null);
    setRelated([]);
    setAdjacent({ previous: null, next: null });
    Promise.all([
      fetch(`${API}/api/blog/${slug}`).then(r => r.json()),
      fetch(`${API}/api/blog/${slug}/adjacent`).then(r => r.json()).catch(() => ({ previous: null, next: null })),
      fetch(`${API}/api/blog/related/${slug}?limit=4`).then(r => r.json()).catch(() => []),
    ]).then(([postData, adj, rel]) => {
      setPost(postData);
      setAdjacent(adj || { previous: null, next: null });
      setRelated(Array.isArray(rel) ? rel : []);
      setLoading(false);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }).catch(() => setLoading(false));

    /* Register a view once per session per slug + track read-time.

       Session-scoped dedupe keeps the counter honest without server-side
       fingerprinting — a returning reader in a new tab (or the next day)
       will count as a fresh view. The sessionId (random, non-PII) lets the
       backend group heartbeats for the same reading session so we can
       compute average read time. GDPR-compliant: no cookies, no personal
       data, no IP/name/email. */
    let sessionId = null;
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionId = sessionStorage.getItem('casaothers.sessionId');
        if (!sessionId) {
          sessionId = (crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36));
          sessionStorage.setItem('casaothers.sessionId', sessionId);
        }
        const viewKey = `casaothers.blogViewed.${slug}`;
        if (!sessionStorage.getItem(viewKey)) {
          sessionStorage.setItem(viewKey, '1');
          fetch(`${API}/api/blog/${slug}/view`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sessionId,
              referrer: document.referrer || '',
              language: (navigator.language || '').slice(0, 5),
            }),
          }).catch(() => {});
        }
      }
    } catch { /* private mode / quota — noop */ }

    if (!sessionId) return; /* no session storage = no heartbeat tracking */

    /* Heartbeat loop: accumulate active read time in ms and post it to the
       backend every 15s. Only counts while the tab is visible. Uses
       navigator.sendBeacon on unload so the final read time survives even
       when the user closes the tab mid-flush. */
    let activeMs = 0;
    let lastTick = document.visibilityState === 'visible' ? Date.now() : null;
    const TICK = 1000;
    const FLUSH_EVERY = 15_000;
    let lastFlushed = 0;

    const flush = (useBeacon = false) => {
      if (activeMs <= lastFlushed) return;
      lastFlushed = activeMs;
      const body = JSON.stringify({ sessionId, readMs: activeMs });
      const url = `${API}/api/blog/${slug}/heartbeat`;
      if (useBeacon && navigator.sendBeacon) {
        try {
          navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
        } catch { /* noop */ }
      } else {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
      }
    };

    const tick = () => {
      if (document.visibilityState === 'visible' && lastTick) {
        const now = Date.now();
        activeMs += now - lastTick;
        lastTick = now;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        lastTick = Date.now();
      } else {
        tick();
        lastTick = null;
        flush(); /* fire-and-forget on tab hide */
      }
    };

    const onUnload = () => { tick(); flush(true); };

    const tickInterval = setInterval(tick, TICK);
    const flushInterval = setInterval(() => { tick(); flush(); }, FLUSH_EVERY);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onUnload);
    window.addEventListener('beforeunload', onUnload);

    return () => {
      clearInterval(tickInterval);
      clearInterval(flushInterval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onUnload);
      window.removeEventListener('beforeunload', onUnload);
      tick();
      flush(true); /* final flush when navigating to a different post */
    };
  }, [slug]);

  const content = useMemo(() => {
    if (!post) return '';
    return (!isNL && post.content_en) ? post.content_en : post.content;
  }, [post, isNL]);

  /* Outline of the rendered markdown — drives the TOC + scroll-spy. */
  const headings = useMemo(() => extractHeadings(content), [content]);
  const showToc = headings.length >= 4;
  /* Collapse-by-default on small screens so the article hits the fold faster.
     We only fold once on mount; user toggles win after that. */
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 900) setTocOpen(false);
  }, []);

  /* Scroll progress bar — fixed at top, gold fill */
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setScrollPct(max > 0 ? Math.min(100, (h.scrollTop / max) * 100) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* IntersectionObserver-based scroll-spy. We observe every heading element
     after content renders and highlight the closest one in the TOC. */
  useEffect(() => {
    if (!showToc || !articleRef.current) return;
    const els = headings
      .map(h => document.getElementById(h.id))
      .filter(Boolean);
    if (els.length === 0) return;
    const seen = new Map();
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => seen.set(e.target.id, e.isIntersecting && e.intersectionRatio));
      // Pick the first heading currently intersecting; fall back to the last
      // one above the viewport so jumping past quick sections still highlights
      // the right entry.
      let current = '';
      for (const h of headings) {
        if (seen.get(h.id)) { current = h.id; break; }
      }
      if (!current) {
        for (let i = headings.length - 1; i >= 0; i--) {
          const el = document.getElementById(headings[i].id);
          if (el && el.getBoundingClientRect().top < 120) { current = headings[i].id; break; }
        }
      }
      if (current) setActiveId(current);
    }, { rootMargin: '-100px 0px -65% 0px', threshold: [0, 0.5, 1] });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [headings, showToc, post]);

  const scrollToHeading = useCallback((id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.pageYOffset - 90;
    window.scrollTo({ top, behavior: 'smooth' });
    history.replaceState(null, '', `#${id}`);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const formatDate = (iso) => new Date(iso).toLocaleDateString(
    isNL ? 'nl-NL' : 'en-GB',
    { year: 'numeric', month: 'long', day: 'numeric' }
  );

  /* Loading / Not-found states */
  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <p style={styles.loadingText}>{t('common.loading')}</p>
        </div>
      </div>
    );
  }
  if (!post || post.detail) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <p style={styles.errorText}>{isNL ? 'Artikel niet gevonden' : 'Article not found'}</p>
          <Link to="/blog" style={styles.backLink}>
            <ArrowLeft size={18} /> {t('blog.backToBlog')}
          </Link>
        </div>
      </div>
    );
  }

  const title = (!isNL && post.title_en) ? post.title_en : post.title;
  const excerpt = (!isNL && post.excerpt_en) ? post.excerpt_en : post.excerpt;
  const category = post.category ? CATEGORY_BY_ID[post.category] : null;
  const canonical = `${PUBLIC_BASE}/blog/${post.slug}`;

  /* JSON-LD for Article + BreadcrumbList. Search engines use this to render
     rich snippets and to associate the article with its category. */
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `${canonical}#article`,
        headline: title,
        description: excerpt,
        image: [post.cover_image].filter(Boolean),
        datePublished: post.published_at,
        dateModified: post.published_at,
        author: { '@type': 'Organization', name: post.author || 'CasaOthers', url: PUBLIC_BASE },
        publisher: {
          '@type': 'Organization',
          name: 'CasaOthers',
          logo: { '@type': 'ImageObject', url: `${PUBLIC_BASE}/og-image.jpg` },
        },
        mainEntityOfPage: canonical,
        articleSection: category ? (isNL ? category.label : category.labelEn) : undefined,
        keywords: (post.tags || []).join(', '),
        inLanguage: isNL ? 'nl-NL' : 'en-GB',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: isNL ? 'Home' : 'Home', item: PUBLIC_BASE },
          { '@type': 'ListItem', position: 2, name: isNL ? 'Kennisbank' : 'Knowledge Base', item: `${PUBLIC_BASE}/blog` },
          ...(category ? [{
            '@type': 'ListItem', position: 3,
            name: isNL ? category.label : category.labelEn,
            item: `${PUBLIC_BASE}/blog/categorie/${category.id}`,
          }] : []),
          { '@type': 'ListItem', position: category ? 4 : 3, name: title, item: canonical },
        ],
      },
    ],
  };

  /* Share helpers — every path that ends up generating a *social preview*
     must resolve to our OG endpoint so the receiving platform's crawler
     scrapes the per-blog og:image/og:title/og:description. Facebook,
     LinkedIn, X, Telegram, WhatsApp and the OS-native share sheet (which
     is often used to hand the URL to Instagram/Messenger/etc.) all fall
     under this rule.

     Copy-Link and Email use the canonical URL because a human reads
     that string directly — no crawler involved. When the human later
     pastes that clean URL somewhere that DOES render a preview, we accept
     the trade-off (until we ship SSR).

     `shareText` is composed dynamically: for blog posts we prefer the
     excerpt (a natural sentence) over the raw title, so a shared WhatsApp
     message reads like a mini teaser rather than an ALL-CAPS title. */
  const ogShareUrl = `${PUBLIC_BASE}/api/og/blog/${slug}`;
  const teaser = (excerpt && excerpt.trim())
    ? excerpt.trim().replace(/\s+/g, ' ').slice(0, 180)
    : title;
  const shareText = encodeURIComponent(`${teaser} — CasaOthers`);
  const shareTextOnly = encodeURIComponent(teaser);
  const shareUrl = encodeURIComponent(canonical);
  const shareUrlOg = encodeURIComponent(ogShareUrl);
  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  const onNativeShare = async () => {
    try {
      /* Pass the OG URL so whichever app the user picks (WhatsApp,
         Messenger, Instagram DM, ...) will scrape the correct per-blog
         preview. The meta-refresh inside /api/og/blog/{slug} bounces
         the actual click back to the canonical article. */
      await navigator.share({
        title,
        text: teaser,
        url: ogShareUrl,
      });
    } catch { /* user cancelled or unsupported — noop */ }
  };

  const onCopyLink = async () => {
    try {
      /* Copy the CLEAN canonical URL for humans. */
      await navigator.clipboard.writeText(canonical);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch { /* clipboard blocked — silently noop */ }
  };

  return (
    <div style={styles.page}>
      <SEO title={title} description={excerpt} image={post.cover_image} type="article" url={canonical} />
      {/* Article + Breadcrumb structured data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Reading progress bar */}
      <div style={styles.progressBar} data-testid="blog-progress-bar">
        <div style={{ ...styles.progressFill, width: `${scrollPct}%` }} />
      </div>

      <div style={styles.container}>
        {/* Breadcrumbs */}
        <nav style={styles.breadcrumbs} aria-label="Breadcrumb" data-testid="blog-breadcrumbs">
          <Link to="/" style={styles.crumbLink}>Home</Link>
          <ChevronRight size={14} color="#8C8B88" />
          <Link to="/blog" style={styles.crumbLink}>{isNL ? 'Kennisbank' : 'Knowledge base'}</Link>
          {category && (
            <>
              <ChevronRight size={14} color="#8C8B88" />
              <Link to={`/blog/categorie/${category.id}`} style={styles.crumbLink}>
                {isNL ? category.label : category.labelEn}
              </Link>
            </>
          )}
          <ChevronRight size={14} color="#8C8B88" />
          <span style={styles.crumbActive}>{title}</span>
        </nav>

        <div style={styles.layout} className={showToc ? 'blog-detail-grid' : ''}>
          {/* Sidebar TOC (desktop only) */}
          {showToc && (
            <aside style={styles.tocSidebar} data-testid="blog-toc-sidebar">
              <div style={styles.tocBox}>
                <div style={styles.tocHeader}>
                  <List size={16} color="#C5A059" />
                  <span style={styles.tocTitle}>{isNL ? 'Inhoud' : 'Contents'}</span>
                </div>
                <ol style={styles.tocList}>
                  {headings.map(h => (
                    <li key={h.id} style={h.level === 3 ? styles.tocItemSub : styles.tocItem}>
                      <a
                        href={`#${h.id}`}
                        onClick={scrollToHeading(h.id)}
                        style={activeId === h.id ? styles.tocLinkActive : styles.tocLink}
                        data-testid={`toc-link-${h.id}`}
                      >
                        {h.text}
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            </aside>
          )}

          {/* Article */}
          <article ref={articleRef} style={styles.article}>
            <header style={styles.header}>
              {category && (
                <Link
                  to={`/blog/categorie/${category.id}`}
                  style={styles.categoryPill}
                  data-testid="blog-category-pill"
                >
                  {isNL ? category.label : category.labelEn}
                </Link>
              )}
              <h1 style={styles.title} data-testid="blog-detail-title">{title}</h1>
              <div style={styles.meta}>
                <span style={styles.metaItem}><Calendar size={15} color="#8C8B88" /> {formatDate(post.published_at)}</span>
                <span style={styles.metaItem}><User size={15} color="#8C8B88" /> <span data-testid="blog-author">{post.author}</span></span>
                {post.reading_time > 0 && (
                  <span style={styles.metaItem} data-testid="blog-reading-time">
                    <Clock size={15} color="#8C8B88" /> {post.reading_time} {isNL ? 'min leestijd' : 'min read'}
                  </span>
                )}
              </div>
            </header>

            {post.cover_image && (
              <div style={styles.imageContainer}>
                <img src={post.cover_image} alt={title} style={styles.coverImage} data-testid="blog-cover-image" />
              </div>
            )}

            {/* Mobile collapsible TOC — only shown on narrow screens */}
            {showToc && (
              <div style={styles.tocMobile} data-testid="blog-toc-mobile">
                <button
                  type="button"
                  style={styles.tocMobileToggle}
                  onClick={() => setTocOpen(o => !o)}
                  aria-expanded={tocOpen}
                  data-testid="toc-mobile-toggle"
                >
                  <span style={styles.tocMobileLabel}>
                    <List size={16} color="#C5A059" />
                    {isNL ? 'Inhoud van dit artikel' : 'Contents of this article'}
                  </span>
                  {tocOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {tocOpen && (
                  <ol style={styles.tocListMobile}>
                    {headings.map(h => (
                      <li key={h.id} style={h.level === 3 ? styles.tocItemSub : styles.tocItem}>
                        <a href={`#${h.id}`} onClick={scrollToHeading(h.id)} style={styles.tocLink}>
                          {h.text}
                        </a>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}

            <div style={styles.content} data-testid="blog-content">
              <MarkdownRenderer content={content} />
            </div>

            {/* Tags + share row */}
            <div style={styles.bottomBar}>
              {(post.tags || []).length > 0 && (
                <div style={styles.tagWrap} data-testid="blog-tags">
                  {post.tags.map(t => <span key={t} style={styles.tagPill}>#{t}</span>)}
                </div>
              )}
              <div style={styles.shareWrap} data-testid="blog-share">
                <span style={styles.shareLabel}><Share2 size={14} /> {isNL ? 'Deel' : 'Share'}</span>
                {canNativeShare && (
                  <button
                    type="button"
                    onClick={onNativeShare}
                    style={styles.shareBtnPrimary}
                    title={isNL ? 'Delen via app' : 'Share via app'}
                    data-testid="share-native"
                  >
                    <Share2 size={15} />
                  </button>
                )}
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrlOg}`}
                  target="_blank" rel="noopener noreferrer"
                  style={styles.shareBtn}
                  title="Facebook"
                  data-testid="share-facebook"
                >
                  <FacebookMark />
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrlOg}`}
                  target="_blank" rel="noopener noreferrer"
                  style={styles.shareBtn}
                  title="LinkedIn"
                  data-testid="share-linkedin"
                >
                  <LinkedinMark />
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?text=${shareTextOnly}&url=${shareUrlOg}`}
                  target="_blank" rel="noopener noreferrer"
                  style={styles.shareBtn}
                  title="X (Twitter)"
                  data-testid="share-twitter"
                >
                  <XMark />
                </a>
                <a
                  href={`https://t.me/share/url?url=${shareUrlOg}&text=${shareTextOnly}`}
                  target="_blank" rel="noopener noreferrer"
                  style={styles.shareBtn}
                  title="Telegram"
                  data-testid="share-telegram"
                >
                  <Send size={15} />
                </a>
                <a href={`https://wa.me/?text=${shareText}%20${shareUrlOg}`} target="_blank" rel="noopener noreferrer" style={styles.shareBtn} title="WhatsApp" data-testid="share-whatsapp">
                  <MessageCircle size={15} />
                </a>
                <a href={`mailto:?subject=${encodeURIComponent(title)}&body=${shareText}%0A%0A${shareUrl}`} style={styles.shareBtn} title="Email" data-testid="share-email">
                  <Mail size={15} />
                </a>
                <button type="button" onClick={onCopyLink} style={styles.shareBtn} title={isNL ? 'Kopieer link' : 'Copy link'} data-testid="share-copy">
                  {copied ? <Check size={15} color="#22C55E" /> : <Link2 size={15} />}
                </button>
              </div>
            </div>
          </article>
        </div>

        {/* Prev / Next */}
        {(adjacent.previous || adjacent.next) && (
          <nav style={styles.adjacent} aria-label="More articles" data-testid="blog-adjacent">
            {adjacent.previous ? (
              <AdjacentCard post={adjacent.previous} direction="prev" isNL={isNL} formatDate={formatDate} />
            ) : <span />}
            {adjacent.next ? (
              <AdjacentCard post={adjacent.next} direction="next" isNL={isNL} formatDate={formatDate} />
            ) : <span />}
          </nav>
        )}

        {/* Related */}
        {related.length > 0 && (
          <section style={styles.related} data-testid="blog-related">
            <h2 style={styles.relatedTitle}>{isNL ? 'Gerelateerde artikelen' : 'Related articles'}</h2>
            <div style={styles.relatedGrid}>
              {related.map(p => {
                const t2 = (!isNL && p.title_en) ? p.title_en : p.title;
                const ex2 = (!isNL && p.excerpt_en) ? p.excerpt_en : p.excerpt;
                return (
                  <Link key={p.id} to={`/blog/${p.slug}`} style={styles.relCard} data-testid="related-card">
                    <div style={styles.relImgWrap}>
                      <img src={p.cover_image} alt={t2} style={styles.relImg} loading="lazy" />
                    </div>
                    <div style={styles.relContent}>
                      <div style={styles.relMeta}>
                        {formatDate(p.published_at)}
                        {p.reading_time > 0 && <> · {p.reading_time} min</>}
                      </div>
                      <h3 style={styles.relTitle}>{t2}</h3>
                      <p style={styles.relExcerpt}>{ex2}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Back-to-top — only after meaningful scroll */}
        {scrollPct > 25 && (
          <button
            type="button"
            onClick={scrollToTop}
            style={styles.backToTop}
            aria-label="Back to top"
            data-testid="blog-back-to-top"
          >
            <ChevronUp size={22} />
          </button>
        )}
      </div>
    </div>
  );
};

const AdjacentCard = ({ post, direction, isNL, formatDate }) => {
  const title = (!isNL && post.title_en) ? post.title_en : post.title;
  const label = direction === 'prev'
    ? (isNL ? 'Vorige artikel' : 'Previous article')
    : (isNL ? 'Volgende artikel' : 'Next article');
  const Icon = direction === 'prev' ? ArrowLeft : ArrowRight;
  return (
    <Link
      to={`/blog/${post.slug}`}
      style={direction === 'prev' ? styles.adjCardPrev : styles.adjCardNext}
      data-testid={`adjacent-${direction}`}
    >
      <img src={post.cover_image} alt={title} style={styles.adjImg} loading="lazy" />
      <div style={styles.adjContent}>
        <span style={styles.adjLabel}>
          {direction === 'prev' && <Icon size={13} />}
          {label}
          {direction === 'next' && <Icon size={13} />}
        </span>
        <span style={styles.adjTitle}>{title}</span>
        <span style={styles.adjDate}>{formatDate(post.published_at)}</span>
      </div>
    </Link>
  );
};

const styles = {
  page: { minHeight: '100vh', paddingTop: '0', paddingBottom: '4rem', backgroundColor: '#FAF9F6' },
  progressBar: {
    position: 'fixed', top: 0, left: 0, right: 0,
    height: '3px', backgroundColor: 'rgba(234,229,221,0.5)',
    zIndex: 1000, pointerEvents: 'none',
  },
  progressFill: { height: '100%', backgroundColor: '#C5A059', transition: 'width 0.08s ease-out' },
  container: { maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' },
  breadcrumbs: {
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.4rem',
    padding: '1.5rem 0 1rem', fontSize: '0.82rem', fontFamily: "'Manrope', sans-serif",
  },
  crumbLink: { color: '#C5A059', textDecoration: 'none', fontWeight: 500 },
  crumbActive: { color: '#1A1918', fontWeight: 600, maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  layout: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '2rem', alignItems: 'flex-start' },

  tocSidebar: {
    display: 'none',
    position: 'sticky', top: '90px',
    maxHeight: 'calc(100vh - 120px)',
    overflowY: 'auto',
  },
  tocBox: { backgroundColor: 'white', border: '1px solid #EAE5DD', padding: '1.25rem' },
  tocHeader: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.85rem', paddingBottom: '0.65rem', borderBottom: '1px solid #EAE5DD' },
  tocTitle: { fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#1A1918' },
  tocList: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  tocItem: { paddingLeft: 0 },
  tocItemSub: { paddingLeft: '1rem' },
  tocLink: { color: '#585755', textDecoration: 'none', fontSize: '0.85rem', lineHeight: 1.5, display: 'block', padding: '0.2rem 0', borderLeft: '2px solid transparent', paddingLeft: '0.75rem' },
  tocLinkActive: { color: '#C5A059', textDecoration: 'none', fontSize: '0.85rem', lineHeight: 1.5, display: 'block', padding: '0.2rem 0', borderLeft: '2px solid #C5A059', paddingLeft: '0.75rem', fontWeight: 600 },

  article: { backgroundColor: 'white', border: '1px solid #EAE5DD', padding: 'clamp(1.5rem, 4vw, 3rem)' },
  header: { marginBottom: '2rem' },
  categoryPill: {
    display: 'inline-block',
    backgroundColor: '#1A1918', color: '#C5A059',
    padding: '0.3rem 0.7rem', fontSize: '0.7rem',
    fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
    textDecoration: 'none',
    marginBottom: '1rem',
  },
  title: { fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.85rem, 4vw, 2.8rem)', color: '#1A1918', marginBottom: '1.25rem', lineHeight: 1.2, fontWeight: 500 },
  meta: { display: 'flex', gap: '1.25rem', flexWrap: 'wrap' },
  metaItem: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#8C8B88', fontSize: '0.85rem' },

  imageContainer: { width: '100%', height: 'clamp(220px, 40vw, 420px)', overflow: 'hidden', marginBottom: '2rem', border: '1px solid #EAE5DD' },
  coverImage: { width: '100%', height: '100%', objectFit: 'cover' },

  tocMobile: { backgroundColor: '#FAF6EE', border: '1px solid #EAE5DD', marginBottom: '2rem' },
  tocMobileToggle: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: 'transparent', border: 'none',
    padding: '0.85rem 1rem',
    color: '#1A1918', cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: '0.9rem',
  },
  tocMobileLabel: { display: 'inline-flex', alignItems: 'center', gap: '0.5rem' },
  tocListMobile: { listStyle: 'none', padding: '0 1rem 1rem', margin: 0, display: 'flex', flexDirection: 'column', gap: '0.4rem' },

  content: { color: '#1A1918', fontSize: '1.05rem', lineHeight: 1.8 },

  bottomBar: { marginTop: '2.5rem', paddingTop: '1.5rem', borderTop: '1px solid #EAE5DD', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' },
  tagWrap: { display: 'flex', flexWrap: 'wrap', gap: '0.4rem' },
  tagPill: { display: 'inline-block', backgroundColor: '#FAF6EE', color: '#585755', padding: '0.25rem 0.65rem', fontSize: '0.78rem', borderRadius: '999px' },
  shareWrap: { display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' },
  shareLabel: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', color: '#8C8B88', fontSize: '0.78rem', marginRight: '0.4rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' },
  shareBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px', backgroundColor: 'white', border: '1px solid #EAE5DD', color: '#585755', borderRadius: '999px', cursor: 'pointer', textDecoration: 'none' },
  shareBtnPrimary: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px', backgroundColor: '#C5A059', border: '1px solid #C5A059', color: 'white', borderRadius: '999px', cursor: 'pointer', textDecoration: 'none' },
  xMark: { fontSize: '15px', lineHeight: 1, fontWeight: 700, fontFamily: 'system-ui, sans-serif' },

  adjacent: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '1rem', marginTop: '3rem',
  },
  adjCardPrev: { display: 'flex', gap: '1rem', backgroundColor: 'white', border: '1px solid #EAE5DD', padding: '0.85rem', textDecoration: 'none', color: 'inherit', alignItems: 'center' },
  adjCardNext: { display: 'flex', gap: '1rem', backgroundColor: 'white', border: '1px solid #EAE5DD', padding: '0.85rem', textDecoration: 'none', color: 'inherit', alignItems: 'center', textAlign: 'right', flexDirection: 'row-reverse' },
  adjImg: { width: '90px', height: '90px', objectFit: 'cover', flexShrink: 0 },
  adjContent: { display: 'flex', flexDirection: 'column', gap: '0.3rem', minWidth: 0 },
  adjLabel: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', color: '#C5A059', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' },
  adjTitle: { color: '#1A1918', fontFamily: "'Playfair Display', serif", fontSize: '0.98rem', fontWeight: 500, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  adjDate: { color: '#8C8B88', fontSize: '0.75rem' },

  related: { marginTop: '4rem' },
  relatedTitle: { fontFamily: "'Playfair Display', serif", fontSize: '1.6rem', color: '#1A1918', marginBottom: '1.5rem', fontWeight: 500 },
  relatedGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1.5rem' },
  relCard: { backgroundColor: 'white', border: '1px solid #EAE5DD', textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column' },
  relImgWrap: { width: '100%', height: '170px', overflow: 'hidden' },
  relImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  relContent: { padding: '1.1rem 1.25rem 1.25rem' },
  relMeta: { color: '#8C8B88', fontSize: '0.75rem', marginBottom: '0.5rem' },
  relTitle: { fontFamily: "'Playfair Display', serif", fontSize: '1.05rem', color: '#1A1918', marginBottom: '0.5rem', lineHeight: 1.3, fontWeight: 500 },
  relExcerpt: { color: '#585755', fontSize: '0.85rem', lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },

  backToTop: {
    position: 'fixed', right: '1.25rem', bottom: '1.25rem', zIndex: 100,
    width: '44px', height: '44px', borderRadius: '50%',
    backgroundColor: '#1A1918', color: '#C5A059',
    border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
  },

  backLink: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#C5A059', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 500 },
  loadingText: { textAlign: 'center', color: '#8C8B88', padding: '3rem 0' },
  errorText: { textAlign: 'center', color: '#8C8B88', padding: '3rem 0', marginBottom: '2rem' },
};

/**
 * Desktop two-column (TOC | article) only kicks in above 960px. Mobile keeps
 * the single-column flow for better reading. We inject the @media once on
 * module load and target the layout / TOC nodes via data-testid attribute
 * selectors (which React preserves verbatim, unlike className mutations).
 */
if (typeof document !== 'undefined' && !document.getElementById('blog-detail-mq')) {
  const s = document.createElement('style');
  s.id = 'blog-detail-mq';
  s.textContent = `
    [data-testid="blog-toc-sidebar"] { display: none; }
    [data-testid="blog-toc-mobile"] { display: block; }
    @media (min-width: 960px) {
      .blog-detail-grid { grid-template-columns: 240px minmax(0, 1fr) !important; gap: 2.5rem !important; }
      [data-testid="blog-toc-sidebar"] { display: block !important; }
      [data-testid="blog-toc-mobile"] { display: none !important; }
    }
  `;
  document.head.appendChild(s);
}

export default BlogDetail;
